import {
  FILTER_VALUE_SEPARATOR,
  RANGE_FILTER_SEPARATOR,
} from '@aibees/elephant/constants/filters';
import { RangeValue } from './hooks/types';

const companyRevenueMap: Record<string, string> = {
  '$0-$0.1M': '0-99999',
  '$0.1M-$0.5M': '100000-499999',
  '$0.5M-$1M': '500000-999999',
  '$1M-$5M': '1000000-4999999',
  '$5M-$10M': '5000000-9999999',
  '$10M-$25M': '10000000-24999999',
  '$25M-$50M': '25000000-49999999',
  '$50M-$100M': '50000000-99999999',
  '$100M-$500M': '100000000-499999999',
  '$500M-$1B': '500000000-999999999',
  '$1B-$5B': '1000000000-4999999999',
  '$5B-$10B': '5000000000-9999999999',
  '$10B+': '10000000000',
};

export function mappingCompanyRevenue(query: string): string {
  return query
    .split(FILTER_VALUE_SEPARATOR)
    .map((item) => companyRevenueMap[item])
    .join(FILTER_VALUE_SEPARATOR);
}

// Parses one side of a follower/connection bucket label.
// - Flat numbers are returned as-is (e.g. "500" -> 500).
// - K/M/B suffixed values are multiplied. When `isEnd` is true and a suffix is
//   present, subtracts 1 so "1K" as the end of "1K-2K" becomes 999 — matching
//   the BE's bucket semantics where suffixed end is exclusive.
function parseSuffixed(value: string, isEnd: boolean): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const match = trimmed.match(/^(\d+(?:\.\d+)?)\s*([KMB])?$/i);
  if (!match) return undefined;
  const num = Number(match[1]);
  if (!Number.isFinite(num)) return undefined;
  const suffix = match[2]?.toUpperCase();
  let multiplier = 1;
  if (suffix === 'K') multiplier = 1_000;
  else if (suffix === 'M') multiplier = 1_000_000;
  else if (suffix === 'B') multiplier = 1_000_000_000;
  const result = num * multiplier;
  return isEnd && suffix ? result - 1 : result;
}

// Parses a single follower-bucket label like "1-500", "501-1K", "30K+".
function parseFollowerLabel(label: string): RangeValue {
  if (label.endsWith('+')) {
    return { start: parseSuffixed(label.slice(0, -1), false), end: undefined };
  }
  const parts = label.split('-');
  if (parts.length !== 2) return {};
  return {
    start: parseSuffixed(parts[0], false),
    end: parseSuffixed(parts[1], true),
  };
}

// Maps a `^`-separated follower-bucket query string to numeric ranges for the
// BE. Used by company LinkedIn followers and people LinkedIn followers /
// connections filters. Drops any label that can't be parsed.
export function mappingFollowerRanges(query: string): RangeValue[] {
  return query
    .split(FILTER_VALUE_SEPARATOR)
    .map(parseFollowerLabel)
    .filter((r) => r.start !== undefined || r.end !== undefined);
}

// Parses a single `start>end` custom-range string (emitted by the NUMBER_RANGE
// filter component) to a single-element `RangeValue[]`. Empty parts become
// `undefined` (open-ended on that side).
export function mappingCustomRange(query: string): RangeValue[] {
  const [s, e] = query.split(RANGE_FILTER_SEPARATOR);
  return [{ start: s ? Number(s) : undefined, end: e ? Number(e) : undefined }];
}
